/**
 * Le module **Produits communautaires** — ses produits livrés, un par onglet.
 *
 * Maquette : artboard `b_produits`, dont la rangée de puces porte « Tous,
 * Tontines, Comptes joints, Quêtes, SOS ». Deux produits ont une supervision
 * servie : la tontine (lot 5 de `docs/PLAN_TONTINE.md`) et le compte joint
 * (lot 6 de `docs/PLAN_COMPTE_JOINT.md`). L'écran ne montre que ces deux-là
 * plutôt que d'inventer des quêtes et une cagnotte qu'aucune route ne sert, et
 * « Tous » n'existe pas : deux supervisions aux colonnes différentes ne se
 * mêlent pas dans une table.
 *
 * Chaque onglet garde son écran entier — sa liste, sa fiche, ses gestes. Le
 * module de la matrice qui gouverne l'entrée du menu reste `TONTINE` : les deux
 * consultations sont accordées aux mêmes cinq rôles (V102, V106), et un agent
 * à qui l'une serait refusée lirait le refus du serveur dans son onglet.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { JointAccountsScreen } from './JointAccountsScreen.js';
import { TontinesScreen } from './TontinesScreen.js';

/** Les produits livrés, dans l'ordre des puces de la maquette. */
const PRODUITS = ['tontines', 'jointAccounts'] as const;

type Produit = (typeof PRODUITS)[number];

/** Le module, et l'onglet du produit choisi. */
export function CommunityProductsScreen(): ReactNode {
  const { t } = useTranslation();
  const [produit, setProduit] = useState<Produit>('tontines');

  return (
    <div className="community-products">
      <div aria-label={t('communityProducts.tabsLabel')} className="tontines__filters" role="group">
        {PRODUITS.map((valeur) => (
          <button
            aria-pressed={produit === valeur}
            className="tontines__filter"
            data-chosen={produit === valeur}
            data-testid={`community-products-tab-${valeur}`}
            key={valeur}
            onClick={() => {
              setProduit(valeur);
            }}
            type="button"
          >
            {t(`communityProducts.tabs.${valeur}`)}
          </button>
        ))}
      </div>
      {produit === 'tontines' ? <TontinesScreen /> : <JointAccountsScreen />}
    </div>
  );
}
